import { useEffect, useRef } from "react";
import {
  formatMultiColumnContentCards,
  renderRichText,
} from "@/utils/formatters";
import { useRouter } from "next/router";
import BackToTop from "@/components/backToTop";
import PropTypes from "prop-types";
import { MultipleColumnContent } from "@kohler-dsm-library/kohler-modules";
import styles from "./index.module.scss";

export default function PressReleaseArticle({ fields }) {
  const { locale } = useRouter();
  const { title, contentBlocks = [], mediaContacts = [] } = fields || {};
  const contentRef = useRef(null);

  useEffect(() => {
    if (!contentRef.current) return;

    const links = contentRef.current.querySelectorAll("a");

    links.forEach(link => {
      link.removeAttribute("target");
      link.setAttribute("target", "_self");
    });
  }, [contentBlocks]);

  return (
    <>
      <div className="max-w-980 flex flex-col lg:flex-row gap-10 max-w-screen-lg mx-auto min-[767px]:px-[12px] px-[15px] my-[20px] min-[767px]:my-10 text-justify">
        {/* ================= LEFT CONTENT ================= */}
        <div className="lg:w-2/3 w-full">
          <div className="flex flex-col">
            {(contentBlocks || []).map((block, index) => {
              const contentTypeId = block?.sys?.contentType?.sys?.id;
              const data = block?.fields || {};
              const hasImage = !!data.imageUrl;
              const hasText = !!data.text;
              const isCenter = data?.imagePlacement === "center";

              if (contentTypeId === "multipleColumnContent") {
                return (
                  <MultipleColumnContent
                    key={block?.sys?.id || `multiple-column-${index}`}
                    className={styles.pressReleaseMultipleColumnContent}
                    mod={data?.mod}
                    numberOfColumns={data?.numberOfColumns}
                    sliderOnSmallScreens={false}
                    eyebrow={
                      data?.eyebrow && {
                        text: renderRichText(data.eyebrow, locale),
                      }
                    }
                    heading={
                      data?.heading?.fields?.text && {
                        text: data?.heading?.fields?.text,
                        component: "h2",
                      }
                    }
                    copy={
                      data?.copy && {
                        text: renderRichText(data?.copy, locale),
                      }
                    }
                    cards={formatMultiColumnContentCards(data?.cards, locale)}
                  />
                );
              }

              return (
                <div
                  key={block?.sys?.id || index}
                  className="flex flex-col gap-4"
                >
                  {hasImage && (
                    <img
                      src={data.imageUrl?.replace(/%22/g, "")}
                      alt={data.alt || ""}
                      className="mx-auto ml-0"
                    />
                  )}
                  {data?.assets &&
                    data?.assets.map((asset, idx) => (
                      <img
                        key={idx}
                        src={asset?.fields?.file?.url}
                        alt={asset?.fields?.file.fileName || ""}
                        className={`mx-auto ${isCenter ? "" : "ml-0"}`}
                      />
                    ))}
                  {hasText && (
                    <div ref={contentRef}>
                      {renderRichText(data.text, locale)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ================= RIGHT MEDIA CONTACTS ================= */}
        <div className="lg:w-1/3 lg:w-1/3 lg:max-w-[280px] bg-[#e5e5e5] p-6 h-fit p-[24px] font-[HelveticaRoman,_Helvetica,_sans-serif] text-[#232323]">
          <h4 className='text-[24px] font-[HelveticaRoman,_Helvetica,_sans-serif]'>
            {title}
          </h4>
          <div className="flex flex-col w-full lg:w-4/5">
            {(mediaContacts || []).map((contact, index) => {
              const { name, designation, email } = contact?.fields || {};
              return (
                <div
                  key={contact?.sys?.id || index}
                  className="border-t border-[#ccc] mt-3 pt-3"
                >
                  {name && (
                    <p className="!text-[15px] !font-[HelveticaRoman,_Helvetica,_sans-serif] font-[600] text-[#232323] leading-[26px]">
                      {name}
                    </p>
                  )}

                  {designation && (
                    <p className="!text-[15px] font-bold !font-[HelveticaRoman,_Helvetica,_sans-serif] font-[400] text-[#232323] leading-[26px]">
                      {designation}
                    </p>
                  )}

                  {email && (
                    <a
                      href={`mailto:${email}`}
                      className="!text-[15px] !font-[HelveticaRoman,_Helvetica,_sans-serif] leading-[26px] text-[#337ab7] hover:!no-underline"
                    >
                      {email}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <BackToTop topHeight={0} localeProp={locale} />
    </>
  );
}

/* ===================== PROP TYPES ===================== */

PressReleaseArticle.propTypes = {
  fields: PropTypes.shape({
    title: PropTypes.string,

    contentBlocks: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          imageUrl: PropTypes.string,
          alt: PropTypes.string,
          text: PropTypes.object, // Contentful rich text
        }),
      })
    ),

    mediaContacts: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          name: PropTypes.string,
          designation: PropTypes.string,
          email: PropTypes.string,
        }),
      })
    ),
  }).isRequired,
};
